import api from './api';

export interface Collection {
    id?: string;
    name: string;
    imageUrl?: string;
    isActive: boolean;
    isDeleted: boolean;
    sortOrder?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface CollectionProduct {
    id?: string;
    collectionId: string;
    productId: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface CollectionListData {
    items: Collection[];
    pagination?: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
    };
}

export interface CollectionResponse {
    success: boolean;
    message?: string;
    data?: CollectionListData | Collection;
}

export interface CollectionProductListResponse {
    success: boolean;
    message?: string;
    data?: {
        items: CollectionProduct[];
        pagination?: {
            currentPage: number;
            totalPages: number;
            totalItems: number;
            itemsPerPage: number;
        };
    };
}

export interface AddProductToCollectionPayload {
    productId: number | number[];
}

export interface ProductSearchResult {
    productId: number;
    name: string;
}

export const collectionService = {
    /**
     * Search products by name (for adding to collection)
     * GET /api/site/collections/products/search?q=...
     */
    searchProducts: async (q: string): Promise<{ success: boolean; data?: ProductSearchResult[] }> => {
        const response = await api.get('/collections/products/search', { params: { q } });
        return response.data;
    },

    /**
     * Get all collections with pagination
     * GET /api/site/collections
     */
    getCollections: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
    }): Promise<CollectionResponse> => {
        const response = await api.get('/collections', { params });
        return response.data;
    },

    /**
     * Get single collection by ID
     * GET /api/site/collections/:id
     */
    getCollectionById: async (id: string): Promise<CollectionResponse> => {
        const response = await api.get(`/collections/${id}`);
        return response.data;
    },

    /**
     * Create a new collection
     * POST /api/site/collections  (multipart/form-data, field: image)
     */
    createCollection: async (
        data: Pick<Collection, 'name' | 'isActive' | 'isDeleted'>,
        imageFile?: File | null
    ): Promise<CollectionResponse> => {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('isActive', String(data.isActive));
        formData.append('isDeleted', String(data.isDeleted));
        if (imageFile) {
            formData.append('image', imageFile);
        }
        const response = await api.post('/collections', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    /**
     * Update a collection
     * PUT /api/site/collections/:id  (multipart/form-data, field: image optional)
     */
    updateCollection: async (
        id: string,
        data: Partial<Pick<Collection, 'name' | 'isActive' | 'isDeleted'>>,
        imageFile?: File | null
    ): Promise<CollectionResponse> => {
        const formData = new FormData();
        if (data.name !== undefined) formData.append('name', data.name);
        if (data.isActive !== undefined) formData.append('isActive', String(data.isActive));
        if (data.isDeleted !== undefined) formData.append('isDeleted', String(data.isDeleted));
        if (imageFile) {
            formData.append('image', imageFile);
        }
        const response = await api.put(`/collections/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    /**
     * Update a collection's sort order (drag-and-drop reorder)
     * PATCH /api/site/collections/:id/sort-order
     */
    updateSortOrder: async (id: string, sortOrder: number): Promise<CollectionResponse> => {
        const response = await api.patch(`/collections/${id}/sort-order`, { sortOrder });
        return response.data;
    },

    /**
     * Soft-delete a collection
     * DELETE /api/site/collections/:id
     */
    deleteCollection: async (id: string): Promise<{ success: boolean; message?: string }> => {
        const response = await api.delete(`/collections/${id}`);
        return response.data;
    },

    /**
     * Get products in a collection
     * GET /api/site/collections/:id/products
     */
    listCollectionProducts: async (
        collectionId: string,
        params?: {
            page?: number;
            limit?: number;
        }
    ): Promise<CollectionProductListResponse> => {
        const response = await api.get(`/collections/${collectionId}/products`, { params });
        return response.data;
    },

    /**
     * Add product(s) to a collection
     * POST /api/site/collections/:id/products
     * Body: { productId: number } OR { productId: number[] }
     */
    addProductToCollection: async (
        collectionId: string,
        payload: AddProductToCollectionPayload
    ): Promise<{ success: boolean; message?: string; data?: CollectionProduct | CollectionProduct[] }> => {
        const response = await api.post(`/collections/${collectionId}/products`, payload);
        return response.data;
    },

    /**
     * Remove a product from a collection
     * DELETE /api/site/collections/:id/products/:productId
     */
    removeProductFromCollection: async (
        collectionId: string,
        productId: number
    ): Promise<{ success: boolean; message?: string }> => {
        const response = await api.delete(`/collections/${collectionId}/products/${productId}`);
        return response.data;
    },
};

export default collectionService;
